import { useState, useEffect } from "react";
import { fmtDate } from "@/lib/dates";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Star, ThumbsUp, Gift, MapPin, Link as LinkIcon, Cake, UtensilsCrossed, ExternalLink, Check } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { importantLabel } from "@/lib/profileLabels";
import { Avatar, Loader, EmptyState } from "@/components/common";

const CAT_META = {
  favorites: { label: "Favorite Places", icon: Star },
  likes_dislikes: { label: "Likes & Dislikes", icon: ThumbsUp },
  gift: { label: "Gift Information", icon: Gift },
  important: { label: "Important Information", icon: Lock },
};

export default function PersonProfilePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/connections/${id}/profile`);
        setData(res.data);
      } catch {
        nav(`/connections/${id}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  if (loading) return <Loader />;
  if (!data) return null;

  const s = data.sections || {};
  const anyShared = Object.values(data.permissions).some(Boolean);

  return (
    <div className="iks-fade-up">
      <button onClick={() => nav(`/connections/${id}`)} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm mb-4">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center gap-4 mb-6">
        <Avatar photo={data.person.profile_photo} name={data.person.name} size={64} />
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-50">{data.person.name}</h1>
          <p className="text-slate-400 text-sm">@{data.person.username}</p>
        </div>
      </div>

      {!anyShared && (
        <EmptyState icon={Lock} title="Nothing shared yet" subtitle={`${data.person.name} hasn't shared any profile details with you. They control what you can see.`} testId="nothing-shared" />
      )}

      {s.basic && (
        <div className="iks-card p-5 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-3">Basic Information</p>
          <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
            {s.basic.birthday && <><span className="text-slate-500">Birthday</span><span className="text-slate-200">{fmtDate(s.basic.birthday)}</span></>}
            {s.basic.zodiac && <><span className="text-slate-500 flex items-center gap-1"><Cake size={12} /> Zodiac</span><span className="text-slate-200">{s.basic.zodiac}</span></>}
            {s.basic.pronouns && <><span className="text-slate-500">Pronouns</span><span className="text-slate-200">{s.basic.pronouns}</span></>}
            {s.basic.location && <><span className="text-slate-500 flex items-center gap-1"><MapPin size={12} /> Location</span><span className="text-slate-200">{s.basic.location}</span></>}
            {s.basic.phone && <><span className="text-slate-500">Phone</span><span className="text-slate-200">{s.basic.phone}</span></>}
          </div>
        </div>
      )}

      {s.about && (
        <div className="iks-card p-5 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-2">About</p>
          <p className="text-slate-300 text-sm whitespace-pre-wrap">{s.about}</p>
        </div>
      )}

      {s.items && Object.entries(s.items).map(([cat, list]) => {
        const meta = CAT_META[cat] || { label: cat, icon: Star };
        const Icon = meta.icon;
        return (
          <div key={cat} className="iks-card p-5 mb-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-3 flex items-center gap-1.5"><Icon size={13} /> {meta.label}</p>
            <div className="space-y-2">
              {list.map((it) => (
                <div key={it.id} className="flex items-start gap-3">
                  {it.data?.photo && <img src={fileUrl(it.data.photo)} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-100 text-sm font-medium break-words" data-testid="viewer-item-name">
                      {cat === "important" && <span className="text-sky-300 font-semibold" data-testid="viewer-item-type-label">{importantLabel(it)}: </span>}
                      {it.name}
                    </p>
                    {it.detail && <p className="text-slate-400 text-sm whitespace-pre-line break-words">{it.detail}</p>}
                    {it.data?.order && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {it.data.order.split("\n").map((o) => o.trim()).filter(Boolean).map((o, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-200 text-xs">
                            <UtensilsCrossed size={11} className="text-sky-400/80 shrink-0" />
                            {o}
                          </span>
                        ))}
                      </div>
                    )}
                    {it.data?.notes && <p className="text-slate-500 text-xs whitespace-pre-line break-words">{it.data.notes}</p>}
                    {it.data?.link && <a href={it.data.link} target="_blank" rel="noreferrer" className="text-xs text-sky-400 inline-flex items-center gap-1 mt-0.5"><LinkIcon size={11} /> Link</a>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {s.wishlist && s.wishlist.length > 0 && (
        <div className="iks-card p-5 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-400 mb-3 flex items-center gap-1.5"><Gift size={13} /> WishList</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {s.wishlist.map((it) => <WishCard key={it.id} it={it} />)}
          </div>
        </div>
      )}
    </div>
  );
}

const PRIORITY = { high: "High priority", medium: "Medium priority", low: "Low priority" };
const toHref = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
const linkLabel = (u) => { try { return new URL(toHref(u)).hostname.replace(/^www\./, ""); } catch { return u; } };

function WishCard({ it }) {
  return (
    <div className="iks-card overflow-hidden" data-testid="viewer-wish-item">
      {it.photo && <img src={fileUrl(it.photo)} alt="" className="w-full h-36 object-cover" />}
      <div className="p-3">
        <p className={`text-slate-100 text-sm font-medium break-words ${it.purchased ? "line-through text-slate-500" : ""}`}>{it.name}</p>
        <p className="text-slate-500 text-xs mt-0.5">
          {[it.category, it.price != null ? `$${Number(it.price).toFixed(2)}` : null, PRIORITY[it.priority]].filter(Boolean).join(" · ")}
        </p>
        {it.purchased && <p className="text-emerald-400 text-xs mt-1 inline-flex items-center gap-1"><Check size={12} /> Purchased</p>}
        {it.notes && <p className="text-slate-400 text-xs mt-1.5 whitespace-pre-line break-words" data-testid="viewer-wish-notes">{it.notes}</p>}
        {it.link && (
          <a href={toHref(it.link)} target="_blank" rel="noopener noreferrer" data-testid="viewer-wish-link"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-sky-400 hover:text-sky-300 underline-offset-2 hover:underline break-all">
            <ExternalLink size={13} className="shrink-0" /> {linkLabel(it.link)}
          </a>
        )}
      </div>
    </div>
  );
}
